import { createElement } from "react";

const sortByName = (items) =>
  items.sort((first, second) =>
    (first.name || "")
      .trim()
      .localeCompare((second.name || "").trim(), "es"),
  );

const hasPermission = (permission, permissionViews) => {
  if (!permission) return true;

  const permissions = Array.isArray(permission) ? permission : [permission];
  return permissions.some((item) => permissionViews[item]);
};

export const buildPermissionMap = (views = []) =>
  views.reduce((permissions, view) => {
    if (view.name) permissions[view.name] = true;
    return permissions;
  }, {});

export const filterNavigationItems = (
  items,
  permissionViews,
  depth = 0,
) => {
  const visibleItems = (items || []).reduce((result, item) => {
    const hasChildren = Array.isArray(item.children);
    const children = hasChildren
      ? filterNavigationItems(item.children, permissionViews, depth + 1)
      : [];

    if (hasChildren && !children.length) return result;
    if (!hasChildren && !hasPermission(item.permission, permissionViews)) {
      return result;
    }

    return result.concat(hasChildren ? { ...item, children } : { ...item });
  }, []);

  return depth === 1 ? sortByName(visibleItems) : visibleItems;
};

const normalizePath = (path) => {
  const normalizedPath = String(path || "")
    .split(/[?#]/, 1)[0]
    .replace(/\/+$/, "");

  return normalizedPath || "/";
};

export const isNavigationPathActive = (pathname, item) => {
  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(item.activePath || item.url);

  if (!item.url || targetPath === "/") return currentPath === targetPath;

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const collectNavigationLeaves = (items) =>
  (items || []).flatMap((item) =>
    Array.isArray(item.children)
      ? collectNavigationLeaves(item.children)
      : item.url
        ? [item]
        : [],
  );

const isPathWithin = (pathname, url) => {
  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(url);

  if (targetPath === "/") return false;
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

// Una ruta exige los permisos de la opcion de menu mas especifica que la
// contiene. Las rutas sin opcion de menu conservan el acceso actual.
export const canAccessPath = (pathname, items, permissionViews) => {
  const matches = collectNavigationLeaves(items).filter((item) =>
    isPathWithin(pathname, item.url),
  );
  if (!matches.length) return true;

  const longestUrl = Math.max(
    ...matches.map((item) => normalizePath(item.url).length),
  );

  return matches
    .filter((item) => normalizePath(item.url).length === longestUrl)
    .some((item) => hasPermission(item.permission, permissionViews || {}));
};

// El texto se envuelve para que ocupe el ancho disponible junto al icono y
// continue en otra linea cuando no cabe.
const buildSidebarLabel = (name) =>
  createElement("span", { className: "nav-link-label" }, name);

const buildSidebarItems = (items, pathname) =>
  (items || []).reduce(
    (branch, item) => {
      if (Array.isArray(item.children)) {
        const childrenBranch = buildSidebarItems(item.children, pathname);
        const isActive = childrenBranch.hasActiveItem;

        branch.items.push({
          ...item,
          name: buildSidebarLabel(item.name),
          // CoreUI opens dropdowns using a partial string match against `url`.
          // A neutral value prevents sibling groups with the same prefix from
          // opening; the current path is used only for the active branch.
          url: isActive ? normalizePath(pathname) : "#",
          attributes: {
            ...item.attributes,
            "aria-expanded": isActive,
            isActive: () => isActive,
          },
          children: childrenBranch.items,
        });
        branch.hasActiveItem = branch.hasActiveItem || isActive;
        return branch;
      }

      const isActive = isNavigationPathActive(pathname, item);
      branch.items.push({
        ...item,
        name: buildSidebarLabel(item.name),
        attributes: {
          ...item.attributes,
          ...(isActive ? { "aria-current": "page" } : {}),
          isActive: () => isActive,
        },
      });
      branch.hasActiveItem = branch.hasActiveItem || isActive;
      return branch;
    },
    { items: [], hasActiveItem: false },
  );

export const buildSidebarNavigation = (navigation, pathname) => ({
  ...(navigation || {}),
  items: buildSidebarItems(navigation?.items, pathname).items,
});

export const closeSiblingNavigationBranches = (target) => {
  const selectedItem = target?.closest?.(".nav-item");
  const siblingItems = selectedItem?.parentElement?.children;

  if (!selectedItem || !siblingItems) return;

  Array.from(siblingItems).forEach((sibling) => {
    if (sibling === selectedItem) return;

    sibling.classList.remove("open");
    sibling
      .querySelectorAll(".nav-dropdown.open")
      .forEach((dropdown) => dropdown.classList.remove("open"));
  });
};
